import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Spinner, Input, Modal, Field, Select } from "./ui";
import { Plus, Trash2, ChevronDown, ChevronRight, Table2, Columns3, GanttChartSquare, X, GripVertical, CornerDownRight, Eye, EyeOff, Zap, Repeat, BarChart3 } from "lucide-react";
import { cn } from "../lib/utils";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  pointerWithin, closestCenter,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { uid, COLUMN_CHOICES, GROUP_COLORS, tint, defaultOptionsFor, BoardMembersContext } from "./board/boardConstants";
import { AddSubitemRow, SortableItemRow, SortableSubitemRow, SubZone, GroupMenu } from "./board/BoardRows";
import KanbanView from "./board/KanbanView";
import GanttView from "./board/GanttView";
import ItemDrawer from "./board/ItemDrawer";
import AutomationsModal from "./board/AutomationsModal";
import RecurringModal from "./board/RecurringModal";
import BoardDashboard from "./board/BoardDashboard";

export default function BoardView({ projectId, color = "#2563EB", milestones = [] }) {
  const { t, lang, user } = useApp();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [addCol, setAddCol] = useState(null);   // {type,label,_scope}
  const [editCol, setEditCol] = useState(null); // working column copy + _scope
  const [focusId, setFocusId] = useState(null);
  const [openItemId, setOpenItemId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [activeId, setActiveId] = useState(null);
  const [promoteOverId, setPromoteOverId] = useState(null);
  const [dropHint, setDropHint] = useState(null); // top-level drag: {kind:'reorder',gid,beforeId} | {kind:'nest',parentId}
  const [members, setMembers] = useState([]);
  const [showAutomations, setShowAutomations] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const dragCol = useRef(null);
  const autoExpand = useRef({ id: null, timer: null });
  const [dragOverCol, setDragOverCol] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const collisionDetection = useCallback((args) => {
    const { active, droppableContainers, droppableRects, pointerCoordinates } = args;
    const aid = active?.id;
    const activeItem = board?.items?.find((i) => i.id === aid);
    if (!activeItem) return closestCenter(args);
    if (activeItem.parent_id) {
      // dragging a subitem: reorder among siblings while inside the parent's subitem area;
      // only allow PROMOTE (top-level rows) once dragged OUT of that area.
      const sibs = board.items.filter((i) => i.parent_id === activeItem.parent_id).map((i) => i.id);
      const tops = board.items.filter((i) => !i.parent_id).map((i) => i.id);
      const zone = droppableRects?.get?.(`subzone-${activeItem.parent_id}`);
      const p = pointerCoordinates;
      const inZone = zone && p && p.x >= zone.left && p.x <= zone.left + zone.width && p.y >= zone.top && p.y <= zone.top + zone.height;
      const allow = inZone ? new Set(sibs) : new Set([...sibs, ...tops]);
      return closestCenter({ ...args, droppableContainers: droppableContainers.filter((d) => allow.has(d.id)) });
    }
    // dragging a top-level item: a subitem-zone (pointer inside it) means NEST; otherwise REORDER among top rows.
    const hasChildren = board.items.some((i) => i.parent_id === activeItem.id);
    if (!hasChildren) {
      const sub = pointerWithin(args).find((c) => String(c.id).startsWith("subzone-") && c.id !== `subzone-${aid}`);
      if (sub) return [sub];
    }
    const tops = new Set(board.items.filter((i) => !i.parent_id).map((i) => i.id));
    return closestCenter({ ...args, droppableContainers: droppableContainers.filter((d) => tops.has(d.id) && d.id !== aid) });
  }, [board]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: list } = await api.get(`/boards?project_id=${projectId}`);
      if (!list.length) { setBoard(null); return; }
      const { data: full } = await api.get(`/boards/${list[0].id}`);
      setBoard(full);
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // deep-link: open an item drawer when ?item=<id> is present (from a mention notification)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const itemId = searchParams.get("item");
    if (itemId && board?.items?.some((i) => i.id === itemId)) {
      setOpenItemId(itemId);
      const next = new URLSearchParams(searchParams);
      next.delete("item");
      setSearchParams(next, { replace: true });
    }
  }, [board, searchParams, setSearchParams]);

  // entity members for the People/Owner picker
  useEffect(() => {
    if (!board?.entity_id) return;
    api.get(`/entity-members?entity_id=${board.entity_id}`).then(({ data }) => setMembers(data)).catch(() => setMembers([]));
  }, [board?.entity_id]);

  // restore persisted collapse/expand state per board
  useEffect(() => {
    if (!board?.id) return;
    try { setCollapsedGroups(new Set(JSON.parse(localStorage.getItem(`bd:${board.id}:cg`) || "[]"))); } catch { /* noop */ }
    try { setExpandedItems(new Set(JSON.parse(localStorage.getItem(`bd:${board.id}:ei`) || "[]"))); } catch { /* noop */ }
  }, [board?.id]);

  const toggleGroup = (gid) => setCollapsedGroups((s) => {
    const n = new Set(s); n.has(gid) ? n.delete(gid) : n.add(gid);
    localStorage.setItem(`bd:${board.id}:cg`, JSON.stringify([...n])); return n;
  });
  const toggleItemExpand = (iid) => setExpandedItems((s) => {
    const n = new Set(s); n.has(iid) ? n.delete(iid) : n.add(iid);
    localStorage.setItem(`bd:${board.id}:ei`, JSON.stringify([...n])); return n;
  });

  const patchItemLocal = (itemId, fields) =>
    setBoard((b) => ({ ...b, items: b.items.map((it) => (it.id === itemId ? { ...it, ...fields } : it)) }));

  // apply server-side automation side-effects (item moved group / cells set / subitems completed)
  const applyEffects = (effects) => {
    if (!effects?.length) return;
    setBoard((b) => ({ ...b, items: b.items.map((it) => {
      const e = effects.find((x) => x.id === it.id);
      if (!e) return it;
      const next = { ...it };
      if (e.group_id !== undefined) next.group_id = e.group_id;
      if (e.values) next.values = e.values;
      if ("recurrence" in e) next.recurrence = e.recurrence;
      return next;
    }) }));
  };
  // append items auto-spawned by recurrence on completion
  const applySpawned = (spawned) => {
    if (!spawned?.length) return;
    setBoard((b) => ({ ...b, items: [...b.items, ...spawned] }));
  };

  const setCell = async (item, colId, val) => {
    patchItemLocal(item.id, { values: { ...(item.values || {}), [colId]: val } });
    const { data } = await api.put(`/board-items/${item.id}`, { values: { [colId]: val } });
    applyEffects(data?._effects);
    applySpawned(data?._spawned);
  };
  // update several cells of one item in a SINGLE request (avoids races that drop values, e.g. Gantt drag)
  const setCells = async (item, partial) => {
    patchItemLocal(item.id, { values: { ...(item.values || {}), ...partial } });
    const { data } = await api.put(`/board-items/${item.id}`, { values: partial });
    applyEffects(data?._effects);
    applySpawned(data?._spawned);
  };
  const setRecurrence = async (item, recurrence) => {
    patchItemLocal(item.id, { recurrence });
    await api.put(`/board-items/${item.id}`, { recurrence });
  };
  // finish-to-start dependencies (Gantt): item.deps = [predecessorId, ...]
  const setDeps = async (item, deps) => {
    patchItemLocal(item.id, { deps });
    await api.put(`/board-items/${item.id}`, { deps });
  };
  const setName = async (item, name) => {
    if (name === item.name) return;
    patchItemLocal(item.id, { name });
    await api.put(`/board-items/${item.id}`, { name });
  };
  const setDescription = async (item, description) => {
    if (description === (item.description || "")) return;
    patchItemLocal(item.id, { description });
    await api.put(`/board-items/${item.id}`, { description });
  };
  const moveItemGroup = async (item, groupId) => {
    patchItemLocal(item.id, { group_id: groupId });
    await api.put(`/board-items/${item.id}`, { group_id: groupId });
  };
  const addItem = async (groupId) => {
    const { data } = await api.post(`/boards/${board.id}/items`, { group_id: groupId, name: lang === "fr" ? "Nouvel élément" : "New item" });
    setBoard((b) => ({ ...b, items: [...b.items, data] }));
    applyEffects(data?._effects);
    setFocusId(data.id);
    return data;
  };
  // create a top-level item with preset cell values (used by the Kanban "+" to set the column's status)
  const addItemWithValues = async (values, name) => {
    const { data } = await api.post(`/boards/${board.id}/items`, { group_id: board.groups[0]?.id, values, name: name || (lang === "fr" ? "Nouvel élément" : "New item") });
    setBoard((b) => ({ ...b, items: [...b.items, data] }));
    applyEffects(data?._effects);
    return data;
  };
  const addSubitem = async (parentItem, name) => {
    const { data } = await api.post(`/boards/${board.id}/items`, { parent_id: parentItem.id, group_id: parentItem.group_id, name });
    setBoard((b) => ({ ...b, items: [...b.items, data] }));
    setExpandedItems((s) => { const n = new Set(s); n.add(parentItem.id); localStorage.setItem(`bd:${board.id}:ei`, JSON.stringify([...n])); return n; });
    return data;
  };
  const deleteItem = async (item) => {
    if (openItemId === item.id) setOpenItemId(null);
    setBoard((b) => ({ ...b, items: b.items.filter((it) => it.id !== item.id && it.parent_id !== item.id) }));
    await api.delete(`/board-items/${item.id}`);
  };

  // ---- drag: reorder top-level items (insertion placeholder) + nest into a subitem zone ----
  const nestItem = (childId, parentId) => {
    if (childId === parentId) return;
    if (board.items.some((i) => i.parent_id === childId)) return; // keep hierarchy to 2 levels
    const parent = board.items.find((i) => i.id === parentId);
    const gid = parent?.group_id;
    patchItemLocal(childId, { parent_id: parentId, group_id: gid });
    setExpandedItems((s) => { const n = new Set(s); n.add(parentId); localStorage.setItem(`bd:${board.id}:ei`, JSON.stringify([...n])); return n; });
    api.put(`/board-items/${childId}`, { parent_id: parentId, group_id: gid });
  };
  const handleDragStart = (e) => setActiveId(e.active.id);
  const byPos = (a, b) => (a.position || 0) - (b.position || 0);
  const cancelAutoExpand = () => { if (autoExpand.current.timer) clearTimeout(autoExpand.current.timer); autoExpand.current = { id: null, timer: null }; };
  const scheduleAutoExpand = (rowId) => {
    if (autoExpand.current.id === rowId) return;
    cancelAutoExpand();
    autoExpand.current = { id: rowId, timer: setTimeout(() => {
      setExpandedItems((s) => { const n = new Set(s); n.add(rowId); localStorage.setItem(`bd:${board.id}:ei`, JSON.stringify([...n])); return n; });
    }, 550) };
  };
  const clearDrag = () => { setActiveId(null); setPromoteOverId(null); setDropHint(null); cancelAutoExpand(); };
  // persist a flat ordering of top-level items (used by the Kanban view)
  const reorderTopItems = (orderedIds) => {
    setBoard((b) => ({ ...b, items: b.items.map((it) => { const p = orderedIds.indexOf(it.id); return p >= 0 ? { ...it, position: p } : it; }) }));
    api.post(`/boards/${board.id}/reorder`, { items: orderedIds.map((id, p) => ({ id, position: p })) });
  };
  const handleDragOver = (e) => {
    const { active, over } = e;
    const a = board.items.find((i) => i.id === active.id);
    if (!over) { setPromoteOverId(null); setDropHint(null); cancelAutoExpand(); return; }
    // subitem being dragged → promote gap (over a top-level row)
    if (a?.parent_id) {
      const o = board.items.find((i) => i.id === over.id);
      setPromoteOverId(o && !o.parent_id ? over.id : null);
      cancelAutoExpand();
      return;
    }
    // top-level item being dragged
    const oid = String(over.id);
    if (oid.startsWith("subzone-")) { setDropHint({ kind: "nest", parentId: oid.slice(8) }); cancelAutoExpand(); return; }
    const overItem = board.items.find((i) => i.id === over.id);
    if (!overItem || overItem.parent_id) { setDropHint(null); cancelAutoExpand(); return; }
    // auto-expand a hovered collapsed row so a nestable item can be dropped into its subitem zone
    const nestable = a && !board.items.some((i) => i.parent_id === a.id);
    if (nestable && overItem.id !== active.id && !expandedItems.has(overItem.id)) scheduleAutoExpand(overItem.id);
    else cancelAutoExpand();
    const g0 = board.groups[0]?.id;
    const gid = overItem.group_id || g0;
    const ordered = board.items.filter((i) => !i.parent_id && (i.group_id || g0) === gid).sort(byPos);
    const overIdx = ordered.findIndex((i) => i.id === overItem.id);
    const dr = e.active.rect.current.translated, orr = e.over.rect;
    const after = dr && orr ? (dr.top + dr.height / 2) > (orr.top + orr.height / 2) : false;
    let beforeId;
    if (!after) beforeId = overItem.id;
    else { let j = overIdx + 1; while (j < ordered.length && ordered[j].id === active.id) j++; beforeId = j < ordered.length ? ordered[j].id : "end"; }
    if (beforeId === active.id) { setDropHint(null); return; }
    setDropHint({ kind: "reorder", gid, beforeId });
  };
  const handleDragEnd = (e) => {
    const { active, over } = e;
    const activeItem = board.items.find((i) => i.id === active.id);
    clearDrag();
    if (!over) return;
    // --- subitem being dragged: reorder among siblings, OR promote onto a top-level row ---
    if (activeItem?.parent_id) {
      if (active.id === over.id) return;
      const overItem = board.items.find((i) => i.id === over.id);
      if (!overItem) return;
      if (overItem.parent_id === activeItem.parent_id) {
        const sibs = board.items.filter((i) => i.parent_id === activeItem.parent_id).sort(byPos).map((i) => i.id);
        const oi = sibs.indexOf(active.id), ni = sibs.indexOf(over.id);
        if (oi < 0 || ni < 0) return;
        const order = arrayMove(sibs, oi, ni);
        setBoard((b) => ({ ...b, items: b.items.map((it) => { const idx = order.indexOf(it.id); return idx >= 0 ? { ...it, position: idx } : it; }) }));
        api.post(`/boards/${board.id}/reorder`, { items: order.map((id, idx) => ({ id, position: idx })) });
        return;
      }
      if (!overItem.parent_id) {
        // PROMOTE: drop a subitem onto a top-level row → it becomes a top-level item after it
        const gid = overItem.group_id || board.groups[0]?.id;
        const tops = board.items.filter((i) => !i.parent_id && (i.group_id || board.groups[0]?.id) === gid && i.id !== active.id).sort(byPos);
        const idx = tops.findIndex((i) => i.id === overItem.id);
        const order = [...tops.slice(0, idx + 1).map((i) => i.id), active.id, ...tops.slice(idx + 1).map((i) => i.id)];
        setBoard((b) => ({ ...b, items: b.items.map((it) => {
          const p = order.indexOf(it.id);
          if (it.id === active.id) return { ...it, parent_id: null, group_id: gid, position: p };
          return p >= 0 ? { ...it, position: p } : it;
        }) }));
        api.post(`/boards/${board.id}/reorder`, { items: order.map((id, p) => (id === active.id ? { id, position: p, group_id: gid, parent_id: null } : { id, position: p, group_id: gid })) });
        return;
      }
      return;
    }
    // --- top-level item dragged ---
    const oid = String(over.id);
    if (oid.startsWith("subzone-")) { nestItem(active.id, oid.slice(8)); return; }
    const overItem = board.items.find((i) => i.id === over.id);
    if (!overItem || overItem.parent_id || over.id === active.id) return;
    const g0 = board.groups[0]?.id;
    const gid = overItem.group_id || g0;
    const list = board.items.filter((i) => !i.parent_id && (i.group_id || g0) === gid && i.id !== active.id).sort(byPos).map((i) => i.id);
    let idx = list.indexOf(over.id);
    if (idx < 0) idx = list.length;
    const dr = e.active.rect.current.translated, orr = e.over.rect;
    if (dr && orr && (dr.top + dr.height / 2) > (orr.top + orr.height / 2)) idx += 1;
    const newOrder = [...list.slice(0, idx), active.id, ...list.slice(idx)];
    setBoard((b) => ({ ...b, items: b.items.map((it) => {
      const p = newOrder.indexOf(it.id);
      if (it.id === active.id) return { ...it, group_id: gid, position: p };
      return p >= 0 ? { ...it, position: p } : it;
    }) }));
    api.post(`/boards/${board.id}/reorder`, { items: newOrder.map((id, p) => ({ id, position: p, group_id: gid })) });
  };
  // nesting is done by dropping onto an expanded item's subitem zone; promote by dropping a subitem on a top row.

  // ---- columns (scope: "main" | "sub") ----
  const colsFor = (scope) => (scope === "sub" ? (board.subitem_columns || []) : board.columns);
  const saveColumns = async (columns, scope = "main") => {
    const key = scope === "sub" ? "subitem_columns" : "columns";
    setBoard((b) => ({ ...b, [key]: columns }));
    await api.put(`/boards/${board.id}`, { [key]: columns });
  };
  const addColumn = async () => {
    if (!addCol) return;
    const scope = addCol._scope || "main";
    const label = (addCol.label || "").trim() || COLUMN_CHOICES.find((c) => c.type === addCol.type)?.label[lang] || addCol.type;
    const col = { id: uid(), type: addCol.type, label, settings: { options: defaultOptionsFor(addCol.type) } };
    await saveColumns([...colsFor(scope), col], scope);
    setAddCol(null);
  };
  const removeColumn = async (colId, scope = "main") => saveColumns(colsFor(scope).filter((c) => c.id !== colId), scope);
  const reorderColumn = (targetId) => {
    const from = dragCol.current; dragCol.current = null; setDragOverCol(null);
    if (!from || from === targetId) return;
    const cols = [...board.columns];
    const fi = cols.findIndex((c) => c.id === from);
    const ti = cols.findIndex((c) => c.id === targetId);
    if (fi < 0 || ti < 0) return;
    const [m] = cols.splice(fi, 1); cols.splice(ti, 0, m);
    saveColumns(cols, "main");
  };

  // ---- option editor ----
  const isOptionCol = (c) => c && ["status", "priority", "dropdown"].includes(c.type);
  const openColEditor = (col, scope = "main") => setEditCol({ ...JSON.parse(JSON.stringify(col)), _scope: scope });
  const setEditOpt = (idx, patch) => setEditCol((c) => { const opts = [...(c.settings?.options || [])]; opts[idx] = { ...opts[idx], ...patch }; return { ...c, settings: { ...c.settings, options: opts } }; });
  const setEditDone = (idx, val) => setEditCol((c) => ({ ...c, settings: { ...c.settings, options: (c.settings?.options || []).map((o, i) => ({ ...o, done: i === idx ? val : (val ? false : o.done) })) } }));
  const removeEditOpt = (idx) => setEditCol((c) => ({ ...c, settings: { ...c.settings, options: (c.settings?.options || []).filter((_, i) => i !== idx) } }));
  const addEditOpt = () => setEditCol((c) => ({ ...c, settings: { ...c.settings, options: [...(c.settings?.options || []), { id: uid(), label: lang === "fr" ? "Nouvelle option" : "New option", color: "#6366F1" }] } }));
  const commitEditCol = async () => { const scope = editCol._scope || "main"; const { _scope, ...col } = editCol; const cols = colsFor(scope).map((c) => (c.id === col.id ? col : c)); setEditCol(null); await saveColumns(cols, scope); };
  const deleteEditCol = async () => { const id = editCol.id; const scope = editCol._scope || "main"; setEditCol(null); await removeColumn(id, scope); };

  // ---- groups ----
  const saveGroups = async (groups) => { setBoard((b) => ({ ...b, groups })); await api.put(`/boards/${board.id}`, { groups }); };
  const saveBoardWip = async (colId, nextMap) => {
    const wip = { ...(board.wip || {}), [colId]: nextMap };
    setBoard((b) => ({ ...b, wip }));
    await api.put(`/boards/${board.id}`, { wip });
  };
  const addGroup = async () => saveGroups([...board.groups, { id: uid(), name: lang === "fr" ? "Nouveau groupe" : "New group", color: GROUP_COLORS[board.groups.length % GROUP_COLORS.length] }]);
  const renameGroup = (g, name) => { if (name === g.name) return; saveGroups(board.groups.map((x) => (x.id === g.id ? { ...x, name } : x))); };
  const colorGroup = (g, c) => saveGroups(board.groups.map((x) => (x.id === g.id ? { ...x, color: c } : x)));
  const deleteGroup = async (g) => {
    if (board.groups.length <= 1) return;
    const fallback = board.groups.find((x) => x.id !== g.id).id;
    const toMove = board.items.filter((it) => !it.parent_id && (it.group_id || board.groups[0]?.id) === g.id);
    const groups = board.groups.filter((x) => x.id !== g.id);
    setBoard((b) => ({ ...b, groups, items: b.items.map((it) => (toMove.some((m) => m.id === it.id) ? { ...it, group_id: fallback } : it)) }));
    await Promise.all(toMove.map((it) => api.put(`/board-items/${it.id}`, { group_id: fallback })));
    await api.put(`/boards/${board.id}`, { groups });
  };

  if (loading) return <div className="py-16 flex justify-center"><Spinner className="w-7 h-7 text-text-secondary" /></div>;
  if (!board) return <p className="text-sm text-text-secondary py-6">{t("board_none")}</p>;

  const subCols = board.subitem_columns || [];
  const colWidth = (c) => (c.type === "progress" || c.type === "timeline" || c.type === "text" ? "min-w-[160px]" : "min-w-[130px]");
  const drawerItem = board.items.find((it) => it.id === openItemId);
  const topItems = (g) => board.items.filter((it) => !it.parent_id && (it.group_id || board.groups[0]?.id) === g.id).sort((a, b) => (a.position || 0) - (b.position || 0));
  const subItems = (pid) => board.items.filter((it) => it.parent_id === pid).sort((a, b) => (a.position || 0) - (b.position || 0));

  // a top-level item with no children is being dragged → subitem zones become nest drop targets
  const activeItem = activeId ? board.items.find((i) => i.id === activeId) : null;
  const nestActiveTop = !!activeItem && !activeItem.parent_id && !board.items.some((i) => i.parent_id === activeId);

  // ----- subitems nested table (wrapped in a SubZone droppable for nesting) -----
  const renderSubitems = (parent) => {
    const subs = subItems(parent.id);
    const gColor = board.groups.find((x) => x.id === parent.group_id)?.color || color;
    return (
      <SubZone parentId={parent.id} colCount={board.columns.length + 2} bg={tint(color, 0.04)}
        highlight={nestActiveTop && activeId !== parent.id}
        active={dropHint?.kind === "nest" && dropHint.parentId === parent.id}
        dragName={activeItem?.name}>
        <div className="overflow-x-auto rounded-lg border bg-background">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b bg-surface">
                <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-text-secondary min-w-[240px]">{t("board_subitem")}</th>
                {subCols.map((c) => (
                  <th key={c.id} className={cn("px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-text-secondary text-center group/scol", colWidth(c))}>
                    <button onClick={() => openColEditor(c, "sub")} data-testid={`board-subcol-menu-${c.id}`} className="inline-flex items-center gap-1 hover:text-text-primary transition-colors">
                      {c.label}<ChevronDown className="w-3 h-3 opacity-0 group-hover/scol:opacity-100 transition-opacity" />
                    </button>
                  </th>
                ))}
                <th className="px-2 py-1.5 w-9">
                  <button onClick={() => setAddCol({ type: "text", label: "", _scope: "sub" })} data-testid="board-add-subcolumn" className="text-text-secondary hover:text-text-primary"><Plus className="w-3.5 h-3.5" /></button>
                </th>
              </tr>
            </thead>
            <tbody>
              <SortableContext items={subs.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                {subs.map((s) => (
                  <SortableSubitemRow key={s.id} sub={s} subCols={subCols} color={gColor} focusId={focusId} colWidth={colWidth}
                    onSetCell={setCell} onSetName={setName} onDelete={deleteItem} onEditColumn={openColEditor} onClearFocus={() => setFocusId(null)} />
                ))}
              </SortableContext>
              <AddSubitemRow onAdd={(name) => addSubitem(parent, name)} colCount={subCols.length + 2} />
            </tbody>
          </table>
        </div>
      </SubZone>
    );
  };

  const renderGroup = (g) => {
    const items = topItems(g);
    const collapsed = collapsedGroups.has(g.id);
    const gapRow = (key) => (
      <tr key={key} data-testid="board-reorder-gap" className="bg-primary/10">
        <td colSpan={board.columns.length + 2} className="p-0">
          <div className="h-9 mx-2 my-1 rounded-md border-2 border-dashed border-primary/70 bg-primary/5 flex items-center gap-2 px-3 text-xs font-medium text-primary">
            <GripVertical className="w-3.5 h-3.5" />
            <span className="truncate">{activeItem?.name}</span>
          </div>
        </td>
      </tr>
    );
    const showGap = (beforeId) => dropHint?.kind === "reorder" && dropHint.gid === g.id && dropHint.beforeId === beforeId;
    return (
      <div className="mb-6 group/grp" data-testid={`board-group-${g.id}`} key={g.id}>
        {/* Group header styled as a colored tab */}
        <div className="flex items-center gap-2 mb-2">
          <div className="inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 rounded-lg shadow-sm" style={{ backgroundColor: g.color }} data-testid={`board-group-tab-${g.id}`}>
            <button onClick={() => toggleGroup(g.id)} data-testid={`board-group-toggle-${g.id}`} className="shrink-0 text-white/90 hover:text-white inline-flex">
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <input defaultValue={g.name} key={g.name} data-testid={`board-group-name-${g.id}`}
              onBlur={(e) => renameGroup(g, e.target.value.trim() || g.name)}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              style={{ width: `${Math.max(4, g.name.length + 1)}ch` }}
              className="text-sm font-bold uppercase tracking-wide text-white bg-transparent outline-none" />
            <span className="text-[11px] font-semibold text-white px-1.5 rounded-full bg-white/25">{items.length}</span>
          </div>
          <GroupMenu group={g} onColor={(c) => colorGroup(g, c)} onDelete={() => deleteGroup(g)} canDelete={board.groups.length > 1} />
        </div>

        {!collapsed && (
          <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={clearDrag}>
            <div className="overflow-x-auto rounded-xl border">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b bg-surface">
                    <th className="text-left px-2 py-2 text-[11px] uppercase tracking-wider font-semibold text-text-secondary min-w-[280px] sticky left-0 bg-surface">{t("board_item")}</th>
                    {board.columns.map((c) => (
                      <th key={c.id} draggable
                        onDragStart={() => { dragCol.current = c.id; }}
                        onDragOver={(e) => { e.preventDefault(); if (dragOverCol !== c.id) setDragOverCol(c.id); }}
                        onDragLeave={() => setDragOverCol((p) => (p === c.id ? null : p))}
                        onDrop={() => reorderColumn(c.id)}
                        data-testid={`board-col-${c.id}`}
                        className={cn("px-2 py-2 text-[11px] uppercase tracking-wider font-semibold text-text-secondary text-center group/col cursor-grab active:cursor-grabbing", colWidth(c), dragOverCol === c.id && "bg-primary/10 border-l-2 border-primary")}>
                        <span className="inline-flex items-center gap-1">
                          <GripVertical className="w-3 h-3 opacity-0 group-hover/col:opacity-60 transition-opacity" />
                          <button onClick={() => openColEditor(c, "main")} data-testid={`board-col-menu-${c.id}`} className="inline-flex items-center gap-1 hover:text-text-primary transition-colors">
                            {c.label}<ChevronDown className="w-3 h-3 opacity-0 group-hover/col:opacity-100 transition-opacity" />
                          </button>
                        </span>
                      </th>
                    ))}
                    <th className="px-2 py-2 w-10">
                      <button onClick={() => setAddCol({ type: "text", label: "", _scope: "main" })} data-testid="board-add-column" className="text-text-secondary hover:text-text-primary" title={t("board_add_column")}><Plus className="w-4 h-4" /></button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext items={items.map((i) => i.id)} strategy={() => null}>
                    {items.map((it) => (
                      <React.Fragment key={it.id}>
                        {showGap(it.id) && gapRow(`gap-${it.id}`)}
                        {promoteOverId === it.id && (
                          <tr data-testid="board-promote-gap" className="bg-primary/10">
                            <td colSpan={board.columns.length + 2} className="p-0">
                              <div className="h-9 mx-2 my-1 rounded-md border-2 border-dashed border-primary/70 bg-primary/5 flex items-center gap-2 px-3 text-xs font-medium text-primary">
                                <CornerDownRight className="w-3.5 h-3.5 rotate-180" />
                                <span className="truncate">{board.items.find((i) => i.id === activeId)?.name}</span>
                              </div>
                            </td>
                          </tr>
                        )}
                        <SortableItemRow item={it} group={g} columns={board.columns}
                          subitems={subItems(it.id)} expanded={expandedItems.has(it.id)} focusId={focusId}
                          promoteTarget={promoteOverId === it.id}
                          onSetCell={setCell} onSetName={setName} onDelete={deleteItem} onToggleExpand={toggleItemExpand}
                          onOpen={setOpenItemId} onEditColumn={openColEditor} onClearFocus={() => setFocusId(null)} renderSubitems={renderSubitems} />
                      </React.Fragment>
                    ))}
                  </SortableContext>
                  {showGap("end") && gapRow("gap-end")}
                  <tr>
                    <td colSpan={board.columns.length + 2} className="px-3 py-1.5">
                      <button onClick={() => addItem(g.id)} data-testid={`board-add-item-${g.id}`} className="inline-flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors">
                        <Plus className="w-3.5 h-3.5" /> {t("board_add_item")}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <DragOverlay>
              {activeId ? (
                <div className="px-3 py-2 rounded-lg border bg-surface shadow-2xl text-sm font-medium inline-flex items-center gap-2 max-w-sm">
                  <GripVertical className="w-4 h-4 text-text-secondary" />
                  <span className="truncate">{board.items.find((i) => i.id === activeId)?.name}</span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    );
  };

  return (
    <BoardMembersContext.Provider value={members}>
    <div data-testid="board-view">
      <BoardToolbar t={t} view={view} setView={setView} board={board}
        onAutomations={() => setShowAutomations(true)} onRecurring={() => setShowRecurring(true)}
        onAddGroup={addGroup} />

      {view === "table" && board.groups.map((g) => renderGroup(g))}
      {view === "kanban" && <KanbanView board={board} onSetCell={setCell} onReorder={reorderTopItems} onAddItem={addItemWithValues} onOpen={setOpenItemId} boardWip={board.wip || {}} onSaveWip={saveBoardWip} />}
      {view === "gantt" && <GanttView board={board} milestones={milestones} onSetCells={setCells} onSetDeps={setDeps} onOpen={setOpenItemId}
        onAddItem={async () => { const d = await addItem(board.groups[0]?.id); if (d) setOpenItemId(d.id); }}
        onAddSubitem={async (parent) => { const d = await addSubitem(parent, lang === "fr" ? "Nouveau sous-élément" : "New subitem"); if (d) setOpenItemId(d.id); }} />}
      {view === "dashboard" && <BoardDashboard board={board} members={members} />}

      <AddColumnModal t={t} lang={lang} addCol={addCol} setAddCol={setAddCol} onAdd={addColumn} />

      <ColumnEditorModal t={t} editCol={editCol} setEditCol={setEditCol} isOptionCol={isOptionCol}
        setEditOpt={setEditOpt} setEditDone={setEditDone} removeEditOpt={removeEditOpt}
        addEditOpt={addEditOpt} commitEditCol={commitEditCol} deleteEditCol={deleteEditCol} />

      {/* Item drawer */}
      {drawerItem && (
        <ItemDrawer key={drawerItem.id} board={board} item={drawerItem} members={members} me={user?.user_id}
          subCols={subCols} byPos={byPos} onClose={() => setOpenItemId(null)}
          onSetName={setName} onSetDescription={setDescription} onSetCell={setCell} onMoveGroup={moveItemGroup}
          onAddSubitem={addSubitem} onOpenItem={setOpenItemId} openColEditor={openColEditor} onSetRecurrence={setRecurrence} />
      )}

      {showAutomations && (
        <AutomationsModal board={board} members={members} onClose={() => setShowAutomations(false)} />
      )}

      {showRecurring && (
        <RecurringModal board={board} onClose={() => setShowRecurring(false)} onSpawned={(it) => applySpawned([it])} />
      )}
    </div>
    </BoardMembersContext.Provider>
  );
}


function WatchButton({ boardId }) {
  const { t, refreshInbox } = useApp();
  const [watching, setWatching] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(`/boards/${boardId}/watch`).then(({ data }) => { if (on) setWatching(data.watching); }).catch(() => {});
    return () => { on = false; };
  }, [boardId]);
  const toggle = async () => {
    const { data } = await api.post(`/boards/${boardId}/watch`);
    setWatching(data.watching);
    refreshInbox?.();
  };
  if (watching === null) return null;
  return (
    <Button size="sm" variant={watching ? "default" : "outline"} onClick={toggle} data-testid="board-watch-btn"
      title={watching ? t("board_watching") : t("board_watch")}>
      {watching ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {watching ? t("board_watching") : t("board_watch")}
    </Button>
  );
}

// Top toolbar: view tabs (table/kanban/gantt/dashboard) + watch / automations / recurring / add-group.
function BoardToolbar({ t, view, setView, board, onAutomations, onRecurring, onAddGroup }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="inline-flex items-center gap-1 p-1 rounded-lg border bg-surface" data-testid="board-view-tabs">
        {[
          { k: "table", label: t("board_view_table"), Icon: Table2 },
          { k: "kanban", label: t("board_view_kanban"), Icon: Columns3 },
          { k: "gantt", label: t("board_view_gantt"), Icon: GanttChartSquare },
          { k: "dashboard", label: t("board_view_dashboard"), Icon: BarChart3 },
        ].map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setView(k)} data-testid={`board-view-${k}`}
            className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
              view === k ? "bg-text-primary text-background" : "text-text-secondary hover:text-text-primary")}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <WatchButton boardId={board.id} />
        <Button size="sm" variant="outline" onClick={onAutomations} data-testid="board-automations-btn" title={t("autom_title")}>
          <Zap className="w-3.5 h-3.5" /> {t("autom_button")}
        </Button>
        <Button size="sm" variant="outline" onClick={onRecurring} data-testid="board-recurring-btn" title={t("recur_title")}>
          <Repeat className="w-3.5 h-3.5" /> {t("recur_button")}
        </Button>
        {view === "table" && <Button size="sm" variant="outline" onClick={onAddGroup} data-testid="board-add-group"><Plus className="w-3.5 h-3.5" /> {t("board_add_group")}</Button>}
      </div>
    </div>
  );
}

// Modal to add a board column (main or subitem scope).
function AddColumnModal({ t, lang, addCol, setAddCol, onAdd }) {
  return (
    <Modal open={!!addCol} onClose={() => setAddCol(null)} title={addCol?._scope === "sub" ? t("board_add_subcolumn") : t("board_add_column")}
      footer={<>
        <Button variant="outline" onClick={() => setAddCol(null)}>{t("cancel")}</Button>
        <Button onClick={onAdd} data-testid="board-column-save">{t("add")}</Button>
      </>}>
      <Field label={t("board_column_type")}>
        <Select value={addCol?.type || "text"} onChange={(e) => setAddCol((a) => ({ ...a, type: e.target.value }))} data-testid="board-column-type">
          {COLUMN_CHOICES.map((c) => <option key={c.type} value={c.type}>{c.label[lang]}</option>)}
        </Select>
      </Field>
      <Field label={t("board_column_label")}>
        <Input value={addCol?.label || ""} onChange={(e) => setAddCol((a) => ({ ...a, label: e.target.value }))} data-testid="board-column-label" placeholder={t("board_column_label")} />
      </Field>
    </Modal>
  );
}

// Modal to edit a column label + (for status/priority/dropdown) its options.
function ColumnEditorModal({ t, editCol, setEditCol, isOptionCol, setEditOpt, setEditDone, removeEditOpt, addEditOpt, commitEditCol, deleteEditCol }) {
  return (
    <Modal open={!!editCol} onClose={() => setEditCol(null)} title={t("board_edit_column")}
      footer={<>
        <Button variant="ghost" onClick={deleteEditCol} data-testid="board-col-delete" className="text-red-400 hover:bg-red-500/15"><Trash2 className="w-3.5 h-3.5" /> {t("delete")}</Button>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => setEditCol(null)}>{t("cancel")}</Button>
        <Button onClick={commitEditCol} data-testid="board-col-editor-save">{t("save")}</Button>
      </>}>
      <Field label={t("board_column_label")}>
        <Input value={editCol?.label || ""} onChange={(e) => setEditCol((c) => ({ ...c, label: e.target.value }))} data-testid="board-col-edit-label" />
      </Field>
      {isOptionCol(editCol) && (
        <Field label={t("board_options")}>
          <div className="space-y-2">
            {(editCol.settings?.options || []).map((o, idx) => (
              <div key={o.id} className="flex items-center gap-2" data-testid={`col-opt-${o.id}`}>
                <input type="color" value={o.color || "#6366F1"} onChange={(e) => setEditOpt(idx, { color: e.target.value })} data-testid={`col-opt-color-${o.id}`} className="w-8 h-8 rounded-md cursor-pointer bg-transparent border-0 p-0 shrink-0" title={t("board_color")} />
                <Input value={o.label} onChange={(e) => setEditOpt(idx, { label: e.target.value })} data-testid={`col-opt-label-${o.id}`} className="flex-1" />
                {editCol.type === "status" && (
                  <label className="text-[11px] text-text-secondary flex items-center gap-1 whitespace-nowrap cursor-pointer" title={t("board_done_hint")}>
                    <input type="checkbox" checked={!!o.done} onChange={(e) => setEditDone(idx, e.target.checked)} data-testid={`col-opt-done-${o.id}`} /> {t("board_done_state")}
                  </label>
                )}
                <button onClick={() => removeEditOpt(idx)} data-testid={`col-opt-remove-${o.id}`} className="text-text-secondary hover:text-red-400 transition-colors shrink-0"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addEditOpt} data-testid="col-opt-add"><Plus className="w-3.5 h-3.5" /> {t("board_add_option")}</Button>
          </div>
        </Field>
      )}
    </Modal>
  );
}
